/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * Topology Toolbar
 * Controls for zoom, labels, and view reset
 */

import React from "react";
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
} from "@mui/material";
import { ZoomIn, ZoomOut, RestartAlt, UnfoldMore, UnfoldLess } from "@mui/icons-material";
import { Transform } from "./types";

export interface TopologyToolbarProps {
  transform: Transform;
  showLabels: boolean;
  statusFilter: string;
  typeFilter: string;
  collapsedCount: number;
  availableStatuses: string[];
  availableTypes: string[];
  onTransformChange: (transform: Transform) => void;
  onShowLabelsChange: (show: boolean) => void;
  onStatusFilterChange: (status: string) => void;
  onTypeFilterChange: (type: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onReset: () => void;
}

const ZOOM_PRESETS = [
  { label: "50%", value: 1.224 },
  { label: "100%", value: 2.55 },
  { label: "150%", value: 4.08 },
  { label: "200%", value: 5.7375 },
];

export const TopologyToolbar: React.FC<TopologyToolbarProps> = ({
  transform,
  showLabels,
  statusFilter,
  typeFilter,
  collapsedCount,
  availableStatuses,
  availableTypes,
  onTransformChange,
  onShowLabelsChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onExpandAll,
  onCollapseAll,
  onReset,
}) => {
  const handleZoomPreset = (_event: React.MouseEvent<HTMLElement>, newZoom: number | null) => {
    if (newZoom !== null) {
      onTransformChange({
        ...transform,
        scale: newZoom,
      });
    }
  };

  const handleZoomIn = () => {
    const newScale = Math.min(3, transform.scale * 1.2);
    onTransformChange({
      ...transform,
      scale: newScale,
    });
  };

  const handleZoomOut = () => {
    const newScale = Math.max(0.1, transform.scale * 0.8);
    onTransformChange({
      ...transform,
      scale: newScale,
    });
  };

  // Find closest preset to current scale
  const currentPreset = ZOOM_PRESETS.reduce((prev, curr) =>
    Math.abs(curr.value - transform.scale) < Math.abs(prev.value - transform.scale) ? curr : prev
  ).value;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 1.5,
        backgroundColor: "#0f172a", // slate-900 (mockup)
        borderRadius: 2,
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        flexWrap: "wrap",
      }}
    >
      {/* Status Filter */}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel sx={{ color: "#94a3b8" }}>Status</InputLabel>
        <Select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          label="Status"
          sx={{
            color: "#e5e7eb",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.2)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.3)",
            },
            "& .MuiSvgIcon-root": {
              color: "#94a3b8",
            },
          }}
        >
          {availableStatuses.map((status) => (
            <MenuItem key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Type Filter */}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel sx={{ color: "#94a3b8" }}>Type</InputLabel>
        <Select
          value={typeFilter}
          onChange={(e) => onTypeFilterChange(e.target.value)}
          label="Type"
          sx={{
            color: "#e5e7eb",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.2)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.3)",
            },
            "& .MuiSvgIcon-root": {
              color: "#94a3b8",
            },
          }}
        >
          {availableTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
          Zoom:
        </Typography>
      </Box>

      {/* Expand/Collapse buttons */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<UnfoldMore />}
        onClick={onExpandAll}
        sx={{
          color: "#e5e7eb",
          borderColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            borderColor: "rgba(255, 255, 255, 0.3)",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
          },
          textTransform: "none",
          fontSize: "0.875rem",
        }}
      >
        Expand All
      </Button>

      <Button
        variant="outlined"
        size="small"
        startIcon={<UnfoldLess />}
        onClick={onCollapseAll}
        sx={{
          color: "#e5e7eb",
          borderColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            borderColor: "rgba(255, 255, 255, 0.3)",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
          },
          textTransform: "none",
          fontSize: "0.875rem",
        }}
      >
        {collapsedCount > 0 ? `Collapse (${collapsedCount})` : 'Collapse All'}
      </Button>

      <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />

      {/* Zoom controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Zoom Out">
          <IconButton onClick={handleZoomOut} size="small" sx={{ color: "#e5e7eb" }}>
            <ZoomOut fontSize="small" />
          </IconButton>
        </Tooltip>

        <ToggleButtonGroup
          value={currentPreset}
          exclusive
          onChange={handleZoomPreset}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              color: "#e5e7eb",
              borderColor: "rgba(255, 255, 255, 0.2)",
              "&.Mui-selected": {
                backgroundColor: "#3b82f6",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#2563eb",
                },
              },
            },
          }}
        >
          {ZOOM_PRESETS.map((preset) => (
            <ToggleButton key={preset.value} value={preset.value}>
              {preset.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Tooltip title="Zoom In">
          <IconButton onClick={handleZoomIn} size="small" sx={{ color: "#e5e7eb" }}>
            <ZoomIn fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />

      {/* Reset button */}
      <Tooltip title="Reset View">
        <IconButton onClick={onReset} size="small" sx={{ color: "#e5e7eb" }}>
          <RestartAlt fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />

      {/* Show labels toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={showLabels}
            onChange={(e) => onShowLabelsChange(e.target.checked)}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#3b82f6",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#3b82f6",
              },
            }}
          />
        }
        label="Show Labels"
        sx={{
          ml: 1,
          color: "#e5e7eb",
          "& .MuiFormControlLabel-label": {
            fontSize: "0.875rem",
          },
        }}
      />
    </Box>
  );
};
